'use client';

import React, { useCallback, useMemo, useRef, useImperativeHandle, useState, forwardRef } from 'react';
import MessageBubble from './MessageBubble';
import StackBubble3D from './StackBubble3D';
import StackContextMenu from './StackContextMenu';
import { buildStackRenderItems, type RenderItem, type StackState } from '@/lib/stackEngine';
import {
  getChatMessageById,
  useChatMessageById,
  useChatMessagesByIds,
  useChatMetaById,
  useChatOrderedIds,
} from '@/app/messages/_lib/chatMessagesStore';

// Комментарий на русском: разделитель по датам (как в Telegram).
function dayStamp(d: Date) {
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

const EMPTY_IDS: number[] = [];

// Комментарий на русском: кеш форматирования дат — не пересчитываем "Сегодня"/"Вчера" при каждом рендере.
let _cachedToday = 0;
let _cachedYesterday = 0;
let _cacheTime = 0;
function formatDayDividerRu(d: Date) {
  const now = Date.now();
  if (now - _cacheTime > 60000) {
    const nowD = new Date(now);
    _cachedToday = dayStamp(nowD);
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    _cachedYesterday = dayStamp(y);
    _cacheTime = now;
  }
  const ds = dayStamp(d);
  if (ds === _cachedToday) return 'Сегодня';
  if (ds === _cachedYesterday) return 'Вчера';
  const nowYear = new Date(now).getFullYear();
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: d.getFullYear() !== nowYear ? 'numeric' : undefined });
}

const DateDivider = React.memo(function DateDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center py-2">
      <span className="px-3 py-1 rounded-full bg-site-surface/60 text-[11px] text-site-secondary">{label}</span>
    </div>
  );
});

export interface MessageItem {
  id: number;
  client_id?: string;
  from_user?: { id: number; display_name?: string; username?: string; is_online?: boolean };
  message?: string;
  created_at: string;
  [key: string]: any;
}

export interface MessageListProps {
  chatKey: string;
  meId: number;
  chatId?: string;
  showAvatar?: boolean;
  bottomInsetPx?: number;
  scrollerRef?: (el: HTMLDivElement | null) => void;
  pinnedMessageId?: number;
  loadingOlder?: boolean;
  loadingMessages?: boolean;
  hasMoreMessages?: boolean;
  linkPreviewsEnabled?: boolean;
  selectionMode?: boolean;
  selectedMsgKeySet?: Set<string>;
  emptyText?: string;
  enableStacking?: boolean;
  onLoadOlder?: () => void | Promise<void>;
  onScrollPositionChange?: (atBottom: boolean, bottomOffset: number) => void;
  onOpenPhotoViewer?: (photoUrl: string, messageId: number) => void;
  onDelete?: (msg: MessageItem) => void;
  onReply?: (msg: MessageItem) => void;
  onReact?: (msgId: number, emoji: string) => void;
  onToggleSelect?: (msg: MessageItem) => void;
  onForward?: (msg: MessageItem) => void;
  onEdit?: (msg: MessageItem) => void;
  onPin?: (msg: MessageItem) => void;
  msgKeyOf?: (msg: MessageItem) => string | null;
  scrollToMessageById?: (id: number) => void;
  togglePinMessage?: (msg: MessageItem) => void;
}

export interface MessageListHandle {
  scrollToBottom: (behavior?: 'auto' | 'smooth') => void;
  scrollToMessageId: (msgId: number, behavior?: 'auto' | 'smooth') => void;
}

type StackItemProps = {
  chatKey: string;
  item: RenderItem;
  meId: number;
  showAvatar: boolean;
  pinnedMessageId: number;
  selectionMode: boolean;
  selectedMsgKeySet?: Set<string>;
  isScrolling: boolean;
  chatId?: string;
  linkPreviewsEnabled?: boolean;
  onOpenPhotoViewer?: (photoUrl: string, messageId: number) => void;
  onDelete?: (msg: MessageItem) => void;
  onReply?: (msg: MessageItem) => void;
  onReact?: (msgId: number, emoji: string) => void;
  onToggleSelect?: (msg: MessageItem) => void;
  onForward?: (msg: MessageItem) => void;
  onEdit?: (msg: MessageItem) => void;
  onPin?: (msg: MessageItem) => void;
  onExpand: (stackId: string) => void;
  onCollapse: (stackId: string) => void;
  onMerge: (stackId: string) => void;
  onOpenMenu: (stackId: string) => void;
};

const StackItem = React.memo(function StackItem(props: StackItemProps) {
  const {
    chatKey,
    item,
    meId,
    showAvatar,
    pinnedMessageId,
    selectionMode,
    selectedMsgKeySet,
    isScrolling,
    chatId,
    linkPreviewsEnabled,
    onOpenPhotoViewer,
    onDelete,
    onReply,
    onReact,
    onToggleSelect,
    onForward,
    onEdit,
    onPin,
    onExpand,
    onCollapse,
    onMerge,
    onOpenMenu,
  } = props;

  const stackMessageIds = item.kind === 'stack' && item.messageIds ? item.messageIds : EMPTY_IDS;
  const stackMessages = useChatMessagesByIds(chatKey, stackMessageIds);
  if (item.kind !== 'stack' || !item.stackId || !item.messageIds || !item.state) return null;
  if (!stackMessages.length) return null;

  const firstMsg = stackMessages[0];
  const isOwn = firstMsg.from_user?.id === meId;
  const peerOnline = showAvatar ? !!(firstMsg.from_user?.is_online) : false;

  return (
    <div className="py-1.5 transform-gpu pr-5">
      <StackBubble3D
        messages={stackMessages as any}
        isOwn={isOwn}
        showAvatar={showAvatar}
        peerOnline={peerOnline}
        stackId={item.stackId}
        isActiveRun={item.state.isActiveRun}
        owner={item.state.owner}
        mode={item.state.mode}
        carouselIndex={item.state.carouselIndex}
        deferHeavy={isScrolling}
        chatId={chatId}
        onExpand={() => onExpand(item.stackId!)}
        onCollapse={() => onCollapse(item.stackId!)}
        onMerge={() => onMerge(item.stackId!)}
        onOpenMenu={() => onOpenMenu(item.stackId!)}
        onOpenPhotoViewer={onOpenPhotoViewer}
        onDelete={onDelete as any}
        onReply={onReply as any}
        onReact={onReact}
        onToggleSelect={onToggleSelect as any}
        onForward={onForward as any}
        onEdit={onEdit as any}
        onPin={onPin as any}
        selectionMode={selectionMode}
        selectedMsgKeySet={selectedMsgKeySet}
        isPinned={stackMessages.some((m) => m.id === pinnedMessageId)}
        linkify={true}
        enableLinkPreview={linkPreviewsEnabled}
      />
    </div>
  );
});

type MessageRowProps = {
  chatKey: string;
  messageId: number;
  prevMessageId?: number;
  meId: number;
  showAvatar: boolean;
  pinnedMessageId: number;
  selectionMode: boolean;
  selectedMsgKeySet?: Set<string>;
  voicePrefetchRank?: Map<number, number>;
  msgKeyOf?: (msg: MessageItem) => string | null;
  ungroupFirstMsgToStackId: Map<number, string>;
  chatId?: string;
  linkPreviewsEnabled?: boolean;
  onOpenPhotoViewer?: (photoUrl: string, messageId: number) => void;
  onDelete?: (msg: MessageItem) => void;
  onReply?: (msg: MessageItem) => void;
  onReact?: (msgId: number, emoji: string) => void;
  onToggleSelect?: (msg: MessageItem) => void;
  onForward?: (msg: MessageItem) => void;
  onEdit?: (msg: MessageItem) => void;
  onPin?: (msg: MessageItem) => void;
  onStackRegroup: (stackId: string) => void;
};

const MessageRow = React.memo(function MessageRow(props: MessageRowProps) {
  const {
    chatKey,
    messageId,
    prevMessageId,
    meId,
    showAvatar,
    pinnedMessageId,
    selectionMode,
    selectedMsgKeySet,
    voicePrefetchRank,
    msgKeyOf,
    ungroupFirstMsgToStackId,
    chatId,
    linkPreviewsEnabled,
    onOpenPhotoViewer,
    onDelete,
    onReply,
    onReact,
    onToggleSelect,
    onForward,
    onEdit,
    onPin,
    onStackRegroup,
  } = props;

  const msg = useChatMessageById(chatKey, messageId);
  const prev = useChatMessageById(chatKey, prevMessageId || -1);
  const prevMsg = prevMessageId ? prev : null;
  if (!msg) return null;

  const mk = msgKeyOf?.(msg);
  const isSel = !!mk && !!selectedMsgKeySet?.has(mk);
  const peerOnline = showAvatar ? !!(msg.from_user?.is_online) : false;

  const curTime = new Date(msg.created_at).getTime();
  const curD = new Date(curTime);
  const curStamp = dayStamp(curD);
  const prevTime = prevMsg ? new Date(prevMsg.created_at).getTime() : 0;
  const prevD = prevTime ? new Date(prevTime) : null;
  const prevStamp = prevD ? dayStamp(prevD) : -1;
  const showDivider = curStamp > 0 && curStamp !== prevStamp;
  const meOwn = msg.from_user?.id === meId;
  const isGrouped = !!prevMsg && prevMsg.from_user?.id === msg.from_user?.id && curTime - prevTime < 60000;

  const dividerLabel = showDivider ? formatDayDividerRu(curD) : '';
  const wrapperClass = `${showDivider ? 'pt-0' : isGrouped ? 'pt-[2px]' : 'pt-3'} transform-gpu`;

  return (
    <div data-msg-id={msg.id} className={wrapperClass}>
      {showDivider ? <DateDivider label={dividerLabel} /> : null}
      <div className="pr-5">
        {(() => {
          const sid = ungroupFirstMsgToStackId.get(msg.id);
          if (!sid) return null;
          return (
            <div className="mb-1">
              <button
                type="button"
                onClick={() => onStackRegroup(sid)}
                className="text-[11px] text-site-accent/90 hover:text-site-accent transition"
                title="Вернуть стэк"
              >
                ↩︎ Сгруппировать обратно
              </button>
            </div>
          );
        })()}
        <MessageBubble
          message={msg as any}
          isOwn={meOwn}
          showAvatar={showAvatar && !meOwn}
          peerOnline={peerOnline}
          isGrouped={isGrouped}
          chatId={chatId}
          onOpenPhotoViewer={onOpenPhotoViewer}
          onDelete={onDelete as any}
          onReply={onReply as any}
          onReact={onReact as any}
          selectionMode={selectionMode}
          isSelected={isSel}
          onToggleSelect={onToggleSelect as any}
          onForward={onForward as any}
          onEdit={onEdit as any}
          onPin={onPin as any}
          isPinned={pinnedMessageId === msg.id}
          linkify={true}
          enableLinkPreview={linkPreviewsEnabled}
          prefetchVoice={voicePrefetchRank?.has(msg.id)}
          prefetchRank={voicePrefetchRank?.get(msg.id)}
        />
      </div>
    </div>
  );
});

const MessageList = forwardRef<MessageListHandle, MessageListProps>(function MessageList(props, ref) {
  const {
    chatKey,
    meId,
    chatId = '',
    showAvatar = false,
    bottomInsetPx = 0,
    scrollerRef,
    pinnedMessageId = 0,
    loadingOlder = false,
    loadingMessages = false,
    hasMoreMessages = true,
    linkPreviewsEnabled = true,
    selectionMode = false,
    selectedMsgKeySet,
    emptyText = 'Нет сообщений.',
    enableStacking = true,
    onLoadOlder,
    onScrollPositionChange,
    onOpenPhotoViewer,
    onDelete,
    onReply,
    onReact,
    onToggleSelect,
    onForward,
    onEdit,
    onPin,
    msgKeyOf,
    togglePinMessage,
  } = props;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const orderedIds = useChatOrderedIds(chatKey);
  const metaById = useChatMetaById(chatKey);

  // Комментарий на русском: стэкирование сообщений.
  const [stackStates, setStackStates] = useState<Map<string, StackState>>(new Map());
  const [activeStackMenuId, setActiveStackMenuId] = useState<string | null>(null);
  const [ungroupedStackIds, setUngroupedStackIds] = useState<Set<string>>(new Set());
  
  // Комментарий на русском: базовая раскладка (без пользовательских override).
  const baseItems = useMemo<RenderItem[]>(() => {
    if (!orderedIds.length) return [];
    if (!enableStacking) return orderedIds.map((id) => ({ kind: 'message' as const, messageId: id }));
    const stackSource: any[] = [];
    for (const id of orderedIds) {
      const meta = metaById[id];
      if (!meta) continue;
      stackSource.push({ id, from_user: { id: meta.fromUserId }, created_at: meta.createdAt });
    }
    return buildStackRenderItems(stackSource as any, { meId });
  }, [orderedIds, metaById, meId, enableStacking]);

  // Комментарий на русском: быстрый доступ к базовому состоянию стэка по id.
  const baseStackById = useMemo(() => {
    const m = new Map<string, RenderItem>();
    baseItems.forEach((it) => {
      if (it.kind === 'stack' && it.stackId) m.set(it.stackId, it);
    });
    return m;
  }, [baseItems]);

  // Комментарий на русском: для ungroup — показываем кнопку \"Сгруппировать обратно\" над первым сообщением стэка.
  const ungroupFirstMsgToStackId = useMemo(() => {
    const m = new Map<number, string>();
    for (const it of baseItems) {
      if (it.kind === 'stack' && it.stackId && ungroupedStackIds.has(it.stackId) && it.messageIds?.length) {
        m.set(it.messageIds[0], it.stackId);
      }
    }
    return m;
  }, [baseItems, ungroupedStackIds]);

  // Комментарий на русском: финальные элементы (ungroup + override стейтов) — без заглушек, сразу.
  const renderItems = useMemo<RenderItem[]>(() => {
    const out: RenderItem[] = [];

    for (const it of baseItems) {
      if (it.kind === 'stack' && it.stackId && it.messageIds?.length) {
        if (ungroupedStackIds.has(it.stackId)) {
          // Комментарий на русском: ungroup — принудительно разворачиваем стэк в обычные сообщения.
          for (const id of it.messageIds) out.push({ kind: 'message', messageId: id });
          continue;
        }
        const customState = stackStates.get(it.stackId);
        out.push(customState ? { ...it, state: customState } : it);
        continue;
      }
      out.push(it);
    }

    return out;
  }, [baseItems, stackStates, ungroupedStackIds]);

  // Комментарий на русском: отслеживание скролла для LOD.
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    // Комментарий на русском: LOD — упрощаем рендеринг при скролле.
    setIsScrolling(true);
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 150);

    // Комментарий на русском: с column-reverse scrollTop может быть отрицательным.
    // scrollTop=0 = начальная позиция (новые внизу).
    // При прокрутке вверх (к старым) scrollTop уходит в минус.
    const scrollableHeight = el.scrollHeight - el.clientHeight;
    const scrollTop = el.scrollTop;
    
    // Комментарий на русском: distanceFromBottom = насколько прокрутили от начальной позиции.
    // Если scrollTop отрицательный, это расстояние от "низа" (новых сообщений).
    const distanceFromBottom = Math.abs(Math.min(0, scrollTop));
    
    // Комментарий на русском: distanceFromTop = сколько осталось до старых сообщений.
    // minScroll это максимально отрицательное значение scrollTop.
    const minScroll = -scrollableHeight;
    const distanceFromTop = Math.abs(scrollTop - minScroll);

    // Комментарий на русском: загрузка старых когда близко к minScroll (scrollTop очень отрицательный).
    if (distanceFromTop < 200 && hasMoreMessages && !loadingRef.current && onLoadOlder) {
      loadingRef.current = true;
      Promise.resolve(onLoadOlder()).finally(() => {
        loadingRef.current = false;
      });
    }

    // Комментарий на русском: atBottom = scrollTop близок к 0 (новые сообщения).
    const atBottom = distanceFromBottom < 50;
    onScrollPositionChange?.(atBottom, distanceFromBottom);
  }, [hasMoreMessages, onLoadOlder, onScrollPositionChange]);

  // Комментарий на русском: с column-reverse скролл по умолчанию внизу, автоскролл не нужен.

  // Комментарий на русском: атомарное обновление стейта стэка (если его не было — создаём из базового).
  const setStackState = useCallback((stackId: string, patch: Partial<StackState>) => {
    if (!stackId) return;
    setUngroupedStackIds((prev) => {
      if (!prev.has(stackId)) return prev;
      const next = new Set(prev);
      next.delete(stackId);
      return next;
    });
    setStackStates((prev) => {
      const next = new Map(prev);
      const base = baseStackById.get(stackId)?.state;
      const cur = next.get(stackId) || base || { mode: 'collapsed', carouselIndex: 0, isActiveRun: false, owner: 'me' };
      next.set(stackId, { ...cur, ...patch });
      return next;
    });
  }, [baseStackById]);

  const handleStackExpand = useCallback((stackId: string) => setStackState(stackId, { mode: 'expanded' }), [setStackState]);
  const handleStackCollapse = useCallback((stackId: string) => setStackState(stackId, { mode: 'collapsed' }), [setStackState]);
  const handleStackMerge = useCallback((stackId: string) => setStackState(stackId, { mode: 'merged' }), [setStackState]);
  const handleStackUngroup = useCallback((stackId: string) => {
    if (!stackId) return;
    setStackStates((prev) => {
      const next = new Map(prev);
      next.delete(stackId);
      return next;
    });
    setUngroupedStackIds((prev) => {
      if (prev.has(stackId)) return prev;
      const next = new Set(prev);
      next.add(stackId);
      return next;
    });
  }, []);

  const handleStackRegroup = useCallback((stackId: string) => {
    if (!stackId) return;
    setUngroupedStackIds((prev) => {
      if (!prev.has(stackId)) return prev;
      const next = new Set(prev);
      next.delete(stackId);
      return next;
    });
  }, []);

  // Комментарий на русском: продакшн-копирование в буфер (clipboard API + fallback).
  const copyTextToClipboard = useCallback(async (text: string) => {
    const t = String(text || '');
    if (!t) return;
    try {
      if (typeof navigator !== 'undefined' && (navigator as any)?.clipboard?.writeText) {
        await (navigator as any).clipboard.writeText(t);
        return;
      }
    } catch {}
    try {
      const ta = document.createElement('textarea');
      ta.value = t;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch {}
  }, []);

  const getStackMessagesById = useCallback((sid: string): MessageItem[] => {
    const it = baseStackById.get(sid);
    const ids = it?.messageIds || [];
    return ids.map((id) => getChatMessageById(chatKey, id)).filter(Boolean) as MessageItem[];
  }, [baseStackById, chatKey]);

  const msgKey = useCallback((m: MessageItem) => {
    const cid = String(m?.client_id || '');
    const mid = String(m?.id || '');
    if (cid) return `c:${cid}`;
    if (mid && mid !== '0') return `m:${mid}`;
    return `t:${m?.created_at || Date.now()}:${m?.from_user?.id || 0}`;
  }, []);

  const voicePrefetchRank = useMemo(() => {
    // Комментарий на русском: в открытом чате заранее считаем waveform для последних 100 голосовых (снизу вверх).
    const m = new Map<number, number>();
    let rank = 0;
    for (let i = orderedIds.length - 1; i >= 0 && rank < 100; i--) {
      const id = orderedIds[i];
      const msg: any = getChatMessageById(chatKey, id);
      if (!msg) continue;
      const raw = String(msg?.message || '');
      const voiceUrl = typeof msg?.voice_url === 'string' ? String(msg.voice_url) : '';
      const eff = voiceUrl || raw;
      const isVoice =
        raw.startsWith('__voice__:') ||
        eff.startsWith('blob:') ||
        eff.startsWith('/uploads/chat_voice/') ||
        eff.startsWith('/media/chat_voice/') ||
        (typeof msg?.type === 'string' && String(msg.type) === 'voice_message');
      if (!isVoice) continue;
      m.set(id, rank);
      rank++;
    }
    return m;
  }, [chatKey, orderedIds]);

  // Комментарий на русском: рендер элемента (сообщение или стэк).
  const renderItem = useCallback(
    (item: RenderItem, index: number) => {
      if (item.kind === 'stack' && item.stackId && item.messageIds && item.state) {
        return (
          <StackItem
            key={item.stackId}
            chatKey={chatKey}
            item={item}
            meId={meId}
            showAvatar={showAvatar}
            pinnedMessageId={pinnedMessageId}
            selectionMode={selectionMode}
            selectedMsgKeySet={selectedMsgKeySet}
            isScrolling={isScrolling}
            chatId={chatId}
            linkPreviewsEnabled={linkPreviewsEnabled}
            onOpenPhotoViewer={onOpenPhotoViewer}
            onDelete={onDelete as any}
            onReply={onReply as any}
            onReact={onReact}
            onToggleSelect={onToggleSelect as any}
            onForward={onForward as any}
            onEdit={onEdit as any}
            onPin={onPin as any}
            onExpand={handleStackExpand}
            onCollapse={handleStackCollapse}
            onMerge={handleStackMerge}
            onOpenMenu={setActiveStackMenuId}
          />
        );
      }

      if (item.kind === 'message' && item.messageId) {
        const prevId =
          index > 0 && renderItems[index - 1].kind === 'message'
            ? renderItems[index - 1].messageId
            : undefined;
        const msg = getChatMessageById(chatKey, item.messageId);
        const key = msg ? msgKey(msg) : `m:${item.messageId}`;
        return (
          <MessageRow
            key={key}
            chatKey={chatKey}
            messageId={item.messageId}
            prevMessageId={prevId}
            meId={meId}
            showAvatar={showAvatar}
            pinnedMessageId={pinnedMessageId}
            selectionMode={selectionMode}
            selectedMsgKeySet={selectedMsgKeySet}
            voicePrefetchRank={voicePrefetchRank}
            msgKeyOf={msgKeyOf}
            ungroupFirstMsgToStackId={ungroupFirstMsgToStackId}
            chatId={chatId}
            linkPreviewsEnabled={linkPreviewsEnabled}
            onOpenPhotoViewer={onOpenPhotoViewer}
            onDelete={onDelete as any}
            onReply={onReply as any}
            onReact={onReact as any}
            onToggleSelect={onToggleSelect as any}
            onForward={onForward as any}
            onEdit={onEdit as any}
            onPin={onPin as any}
            onStackRegroup={handleStackRegroup}
          />
        );
      }

      return null;
    },
    [
      meId,
      showAvatar,
      pinnedMessageId,
      linkPreviewsEnabled,
      voicePrefetchRank,
      selectionMode,
      selectedMsgKeySet,
      msgKeyOf,
      renderItems,
      isScrolling,
      chatId,
      chatKey,
      onOpenPhotoViewer,
      onDelete,
      onReply,
      onReact,
      onToggleSelect,
      onForward,
      onEdit,
      onPin,
      handleStackExpand,
      handleStackCollapse,
      handleStackMerge,
      handleStackRegroup,
      ungroupFirstMsgToStackId,
      voicePrefetchRank,
      msgKey,
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToBottom(behavior = 'auto') {
        const el = scrollContainerRef.current;
        if (!el) return;
        // Комментарий на русском: с column-reverse scrollTop=0 это низ (новые сообщения).
        el.scrollTop = 0;
      },
      scrollToMessageId(msgId, behavior = 'auto') {
        const msgEl = document.querySelector(`[data-msg-id="${msgId}"]`);
        if (msgEl) {
          msgEl.scrollIntoView({ behavior, block: 'center' });
        }
      },
    }),
    []
  );

  const pinnedMessage = useChatMessageById(chatKey, pinnedMessageId);
  const PinnedHeader = useMemo(() => {
    if (pinnedMessageId <= 0 || !pinnedMessage) return null;
    const t = String((pinnedMessage as any)?.message || '');
    return (
      <div className="sticky top-0 z-10 px-3 sm:px-4 pt-1 -mx-3 sm:-mx-4">
        <div className="w-full rounded-xl glass-subtle border border-site-border px-3 py-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const msgEl = document.querySelector(`[data-msg-id="${pinnedMessageId}"]`);
              msgEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
            className="min-w-0 flex-1 text-left"
            title="Перейти к закреплённому"
          >
            <div className="text-[11px] text-site-text">Закреплено</div>
            <div className="text-[11px] text-site-muted truncate">{t || `Сообщение #${pinnedMessageId}`}</div>
          </button>
          <button
            type="button"
            onClick={() => togglePinMessage?.({ id: pinnedMessageId } as any)}
            className="w-8 h-8 rounded-full glass-btn text-site-text/70 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
            title="Открепить"
            aria-label="Открепить"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }, [pinnedMessageId, pinnedMessage, togglePinMessage]);

  const isEmpty = !renderItems.length && !loadingMessages;

  return (
    <div className={`h-full min-h-0 flex flex-col ${isScrolling ? 'stack-lod-simplified' : ''}`}>
      {PinnedHeader}
      {isEmpty ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-site-muted p-4">{loadingMessages ? 'Загрузка сообщений...' : emptyText}</div>
        </div>
      ) : (
        <div
          ref={(el) => {
            scrollContainerRef.current = el;
            scrollerRef?.(el);
          }}
          onScroll={handleScroll}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain messenger-scroll pl-3 pr-3 pb-3 sm:pl-4 sm:pr-3 sm:pb-20"
          style={{
            display: 'flex',
            flexDirection: 'column-reverse',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            willChange: 'transform',
          }}
        >
          {/* Комментарий на русском: с column-reverse первый элемент DOM внизу, поэтому рендерим от новых к старым. */}
          <div data-messenger-bottom-anchor className="messenger-mobile-bottom-pad" style={{ height: Math.max(0, bottomInsetPx || 0) }} />
          {[...renderItems].reverse().map((item, index) => renderItem(item, renderItems.length - 1 - index))}
          {loadingOlder && (
            <div className="text-center text-xs text-site-muted py-2">Загрузка истории...</div>
          )}
        </div>
      )}
      
      {/* Комментарий на русском: контекстное меню для управления стэками */}
      {activeStackMenuId && (
        <StackContextMenu
          isOpen={true}
          onClose={() => setActiveStackMenuId(null)}
          onExpand={() => handleStackExpand(activeStackMenuId)}
          onCollapse={() => handleStackCollapse(activeStackMenuId)}
          onMerge={() => handleStackMerge(activeStackMenuId)}
          onUngroup={() => handleStackUngroup(activeStackMenuId)}
          onCopyAll={async () => {
            const list = getStackMessagesById(activeStackMenuId);
            const txt = list.map((m) => String(m.message || '')).filter(Boolean).join('\n\n');
            await copyTextToClipboard(txt);
          }}
          onForwardAsOne={() => {
            const list = getStackMessagesById(activeStackMenuId);
            if (!list.length) return;
            const first = list[0] as any;
            const last = list[list.length - 1] as any;
            const merged = list.map((m) => String(m.message || '')).filter(Boolean).join('\n\n');
            // Комментарий на русском: пересылаем как одно сообщение через уже существующий forward-модал.
            onForward?.({ ...(first || {}), id: first?.id || 0, message: merged, created_at: last?.created_at || first?.created_at } as any);
          }}
          mode={stackStates.get(activeStackMenuId)?.mode || baseStackById.get(activeStackMenuId)?.state?.mode || 'collapsed'}
          stackId={activeStackMenuId}
        />
      )}
    </div>
  );
});

export default MessageList;
